import { z } from 'zod';
import { taxonomyRoutingSchema } from './taxonomy';

/**
 * Confidence lane (Epic J) assigned by Triage: `high` proceeds fast, `partial`
 * and `low` enter the governed clarification loop, `unclassifiable` escalates to
 * a human operator.
 */
export const confidenceLanes = ['high', 'partial', 'low', 'unclassifiable'] as const;
export const confidenceLaneSchema = z.enum(confidenceLanes);
export type ConfidenceLane = z.infer<typeof confidenceLaneSchema>;

export interface ClassifiableLabel {
  key: string;
  name: string;
  routing: unknown;
}

export interface Classification {
  matched: boolean;
  labelKey?: string;
  labelName?: string;
  targetAgentId?: string;
  score: number;
  confidence: number;
  lane: ConfidenceLane;
}

export interface ClassificationContext {
  /** Agent already selected by the operator/trigger. Used as a strong fallback signal. */
  targetAgentId?: string;
}

/** Map a confidence score to a lane relative to the workflow type's threshold. */
export function laneFor(confidence: number, threshold = 0.7): ConfidenceLane {
  if (confidence <= 0) return 'unclassifiable';
  if (confidence >= threshold) return 'high';
  if (confidence >= threshold * 0.6) return 'partial';
  return 'low';
}

/**
 * Pure keyword-density classification (Epic J1), shared by the API classifier
 * and the runtime Triage activity so both compute identical lanes. Keeps the
 * deterministic interface an LLM classifier can later implement.
 */
export function classifyText(
  labels: ClassifiableLabel[],
  input: string,
  threshold = 0.7,
  context: ClassificationContext = {},
): Classification {
  const haystack = input.toLowerCase();
  let best: { score: number; priority: number; label: ClassifiableLabel } | undefined;
  let agentTarget: { priority: number; label: ClassifiableLabel; targetAgentId: string } | undefined;

  for (const label of labels) {
    const routing = taxonomyRoutingSchema.parse(label.routing ?? {});
    if (context.targetAgentId && routing.targetAgentId === context.targetAgentId) {
      if (!agentTarget || routing.priority > agentTarget.priority) {
        agentTarget = { priority: routing.priority, label, targetAgentId: routing.targetAgentId };
      }
    }

    const score = routing.keywords.reduce(
      (acc, kw) => (kw && haystack.includes(kw.toLowerCase()) ? acc + 1 : acc),
      0,
    );
    if (score === 0) continue;
    if (!best || score > best.score || (score === best.score && routing.priority > best.priority)) {
      best = { score, priority: routing.priority, label };
    }
  }

  if (!best && agentTarget) {
    const confidence = Math.min(0.99, Math.max(threshold, 0.95));
    return {
      matched: true,
      labelKey: agentTarget.label.key,
      labelName: agentTarget.label.name,
      targetAgentId: agentTarget.targetAgentId,
      score: 0,
      confidence,
      lane: 'high',
    };
  }

  if (!best) return { matched: false, score: 0, confidence: 0, lane: 'unclassifiable' };

  const routing = taxonomyRoutingSchema.parse(best.label.routing ?? {});
  const confidence = Math.min(0.99, Math.round((0.45 + 0.18 * best.score) * 100) / 100);
  return {
    matched: true,
    labelKey: best.label.key,
    labelName: best.label.name,
    targetAgentId: routing.targetAgentId,
    score: best.score,
    confidence,
    lane: laneFor(confidence, threshold),
  };
}
